import { memo, useEffect, useState } from 'react';
import { motion, Reorder, useDragControls } from 'framer-motion';
import { Icon } from './Icon.jsx';
import { renderThumbnail } from '../utils/pdfRender.js';
import { useT } from '../i18n/index.js';

function PageCardBase({ page, index, selected, doc, scale, onSelect, onOpen, onRotate, onDelete, onDuplicate }) {
  const t = useT();
  const [thumb, setThumb] = useState(null);
  const controls = useDragControls();

  useEffect(() => {
    let alive = true;
    if (!doc) return;
    renderThumbnail(doc, page.originalIndex, scale, page.rotation).then((url) => {
      if (alive) setThumb(url);
    });
    return () => {
      alive = false;
    };
  }, [doc, page.originalIndex, page.rotation, scale]);

  return (
    <Reorder.Item
      value={page}
      dragListener={false}
      dragControls={controls}
      as="div"
      whileDrag={{ scale: 1.03, zIndex: 5, boxShadow: '0 18px 50px rgba(0,0,0,0.5)' }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className={`page-card ${selected ? 'selected' : ''}`}
      onPointerDown={(e) => {
        if (e.target.closest('.hover-actions')) return;
        controls.start(e);
      }}
      onClick={(e) => onSelect(page.id, e.metaKey || e.ctrlKey || e.shiftKey)}
      onDoubleClick={() => onOpen(page.id)}
    >
      <div className="thumb">
        {thumb ? (
          <motion.img
            src={thumb}
            alt={`Page ${index + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            draggable={false}
          />
        ) : (
          <div className="skeleton" />
        )}
        <div className="hover-actions">
          <button className="btn icon sm" onClick={(e) => { e.stopPropagation(); onOpen(page.id); }} title={t('toolbar.open_editor')} aria-label={t('toolbar.open_editor')}>
            <Icon name="eye" size={14} />
          </button>
          <button className="btn icon sm" onClick={(e) => { e.stopPropagation(); onRotate(page.id, -90); }} title={t('toolbar.rotate_left')} aria-label={t('toolbar.rotate_left')}>
            <Icon name="rotateCCW" size={14} />
          </button>
          <button className="btn icon sm" onClick={(e) => { e.stopPropagation(); onRotate(page.id, 90); }} title={t('toolbar.rotate_right')} aria-label={t('toolbar.rotate_right')}>
            <Icon name="rotateCW" size={14} />
          </button>
          <button className="btn icon sm" onClick={(e) => { e.stopPropagation(); onDuplicate(page.id); }} title={t('toolbar.duplicate')} aria-label={t('toolbar.duplicate')}>
            <Icon name="copy" size={14} />
          </button>
          <button className="btn icon sm danger" onClick={(e) => { e.stopPropagation(); onDelete(page.id); }} title={t('toolbar.delete')} aria-label={t('toolbar.delete')}>
            <Icon name="trash" size={14} />
          </button>
        </div>
      </div>
      <div className="meta">
        <span className="num">{index + 1}</span>
        <span className="muted">·</span>
        <span className="muted">orig. {page.originalIndex + 1}</span>
        <span className="rot chip">{page.rotation}°</span>
      </div>
    </Reorder.Item>
  );
}

export const PageCard = memo(PageCardBase);
